import { Col, Row } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { ResourceNameView } from 'src/components/ResourceNameView/ResourceNameView';
import { PartnerDto } from 'src/generated-api-client';
import { CountryResourceStore } from 'src/stores/Resources/CountryResourceStore';
import { JahezComissionTypeResourceStore } from 'src/stores/Resources/JahezComissionTypeResourceStore';
import { PartnerTypeResourceStore } from 'src/stores/Resources/PartnerTypeResourceStore';
import { SpecializationResourceStore } from 'src/stores/Resources/SpecializationResourceStore';
import { JahezComissionType } from 'src/types/jahezComissionType';

type GeneralInformationTabProps = {
    partner: PartnerDto | null;
};

export const GeneralInformationTab = observer(
    ({ partner }: GeneralInformationTabProps) => {
        return (
            <>
                <Card
                    title={t(
                        'Partner.GeneralInformation',
                        'General information',
                    )}
                >
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <DataView
                                label={t('Partner.ID', 'Partner ID')}
                                value={partner?.number}
                            />
                        </Col>

                        <Col span={12}>
                            <DataView
                                label={t('Partner.CreatedAt', 'Created at')}
                                value={partner?.createdAt}
                                formatValue="dateTimeSeconds"
                            />
                        </Col>

                        <Col span={12}>
                            <DataView
                                label={t('Partner.ArabicName', 'Arabic name')}
                                value={partner?.arabicName}
                            />
                        </Col>

                        <Col span={12}>
                            <DataView
                                label={t('Partner.EnglishName', 'English name')}
                                value={partner?.englishName}
                            />
                        </Col>

                        <Col span={12}>
                            <DataView
                                label={t('Partner.ERPCode', 'ERP code')}
                                value={partner?.erpCode}
                            />
                        </Col>

                        <Col span={12}>
                            <ResourceNameView
                                label={t('Partner.PartnerType', 'Partner type')}
                                value={partner?.partnerType}
                                resourceStore={PartnerTypeResourceStore}
                            />
                        </Col>

                        <Col span={12}>
                            <ResourceNameView
                                label={t(
                                    'Partner.JahezCommissionType',
                                    'Jahez commission type',
                                )}
                                resourceStore={JahezComissionTypeResourceStore}
                                value={partner?.jahezComissionType}
                            />
                        </Col>

                        <Col span={12}>
                            {partner?.jahezComissionType ===
                                JahezComissionType.FIXED_AMOUNT && (
                                <DataView
                                    label={t('Partner.JahezCommission')}
                                    formatValue="currency-sar"
                                    value={partner?.jahezComissionAmount}
                                />
                            )}
                            {partner?.jahezComissionType ===
                                JahezComissionType.PERCENTAGE && (
                                <DataView
                                    label={t('Partner.JahezCommissionPct')}
                                    formatValue="percent"
                                    value={partner?.jahezComissionPct}
                                />
                            )}
                        </Col>

                        <Col span={12}>
                            <DataView
                                label={t('Partner.CrNumber', 'CR number')}
                                value={partner?.crNumber}
                            />
                        </Col>

                        <Col span={12}>
                            <DataView
                                label={t(
                                    'Partner.LicenseNumber',
                                    'License number',
                                )}
                                value={partner?.licenseNumber}
                            />
                        </Col>

                        <Col span={12}>
                            <ResourceNameView
                                label={t('Partner.Country', 'Country')}
                                resourceStore={CountryResourceStore}
                                value={partner?.country}
                            />
                        </Col>
                        <Col span={12}>
                            <ResourceNameView
                                label={t(
                                    'Partner.Specialization',
                                    'Specialization',
                                )}
                                resourceStore={SpecializationResourceStore}
                                value={partner?.specialization}
                            />
                        </Col>

                        <Col span={24}>
                            <DataView
                                label={t('Partner.Description', 'Description')}
                                value={partner?.description}
                                formatValue="textPreWrap"
                            />
                        </Col>
                    </Row>
                </Card>
                <Card title={t('Partner.Communication', 'Communication')}>
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <DataView
                                label={t(
                                    'Partner.CompanySupportEmail',
                                    'Company support email',
                                )}
                                value={partner?.contact?.supportEmail}
                            />
                        </Col>
                        <Col span={12}>
                            <DataView
                                label={t(
                                    'Partner.CompanySupportPhone',
                                    'Company support phone',
                                )}
                                value={partner?.contact?.supportPhone}
                            />
                        </Col>
                        <Col span={12}>
                            <DataView
                                label={t(
                                    'Partner.ContactPersonInfo',
                                    'Contact person info',
                                )}
                                value={partner?.contact?.contactPersonInfo}
                                formatValue="textPreWrap"
                            />
                        </Col>
                    </Row>
                </Card>
            </>
        );
    },
);
